/**
 * Family Guide Page - Instructions for Frick, Frack & Bubba
 * =========================================================
 * A guide for the whole family to use CodeForge together!
 * Each family member has their own role and instructions.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Container, 
  Activity,
  Brain,
  LogOut,
  Flame,
  Terminal,
  Star,
  Settings,
  Trophy,
  Users,
  Lightbulb,
  Target,
  Shield,
  Wrench,
  Code2,
  Database,
  Rocket,
  HelpCircle,
  CheckCircle,
  ChevronRight,
  Heart,
  Zap,
  BookOpen
} from "lucide-react";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "GUIDES", icon: <Trophy className="w-5 h-5" />, path: "/tutorials" },
    { id: "family", label: "FAMILY GUIDE", icon: <Users className="w-5 h-5" />, path: "/family" },
    { id: "settings", label: "SETTINGS", icon: <Settings className="w-5 h-5" />, path: "/settings" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Checklist Item
const ChecklistItem = ({ children, done = false }) => (
  <div className="flex items-start gap-3 py-2">
    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${done ? 'bg-green-500' : 'border border-white/20'}`}>
      {done && <CheckCircle className="w-4 h-4 text-white" />}
    </div>
    <span className={`font-mono text-sm ${done ? 'text-muted-foreground line-through' : 'text-white'}`}>
      {children}
    </span>
  </div>
);

const FamilyGuidePage = () => {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="family" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4">
            <h1 
              className="text-2xl font-bold text-white uppercase tracking-tight flex items-center gap-3"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="family-guide-title"
            >
              <Users className="w-7 h-7 text-primary" />
              FAMILY GUIDE
            </h1>
            <p className="text-xs font-mono text-muted-foreground mt-1">
              Instructions for Frick, Frack & Bubba | ai.daniejoye.com
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-primary/10 via-green-500/10 to-blue-500/10 border border-primary/20 p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/20">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  WELCOME TO THE FAMILY GARAGE!
                </h3>
                <p className="text-muted-foreground font-mono text-sm">
                  CodeForge is your family's digital workshop. Each member has a special role.
                  Work together, challenge each other, and build amazing things!
                </p>
              </div>
            </div>
          </div>

          {/* Family Member Tabs */}
          <Tabs defaultValue="frick" className="w-full">
            <TabsList className="bg-[#16191F] border border-white/5 p-1 grid grid-cols-3">
              <TabsTrigger value="frick" className="font-mono text-sm data-[state=active]:bg-primary data-[state=active]:text-black">
                <span className="w-2 h-2 bg-primary rounded-full mr-2" />
                FRICK (8)
              </TabsTrigger>
              <TabsTrigger value="frack" className="font-mono text-sm data-[state=active]:bg-green-500 data-[state=active]:text-black">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2" />
                FRACK (10)
              </TabsTrigger>
              <TabsTrigger value="bubba" className="font-mono text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-black">
                <span className="w-2 h-2 bg-blue-400 rounded-full mr-2" />
                BUBBA (Dad)
              </TabsTrigger>
            </TabsList>

            {/* FRICK's Guide */}
            <TabsContent value="frick" className="mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Role Card */}
                <div className="bg-[#16191F] border border-primary/30 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <Lightbulb className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        FRICK - THE ARCHITECT
                      </h3>
                      <p className="text-xs text-muted-foreground font-mono">Age 8 | Curious Builder</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground font-mono text-sm mb-4">
                    You're the idea person! You ask "why?" and "what if?" 
                    Your job is to design what we're building and write the PHP code.
                  </p>
                  <div className="bg-black/30 p-4 border border-white/5">
                    <h4 className="text-white font-bold mb-2">YOUR SUPERPOWERS:</h4>
                    <ul className="space-y-1 text-sm font-mono text-muted-foreground">
                      <li>• Asking great questions</li>
                      <li>• Coming up with cool ideas</li>
                      <li>• Writing PHP code</li>
                      <li>• Designing how things work</li>
                    </ul>
                  </div>
                </div>

                {/* Daily Checklist */}
                <div className="bg-[#16191F] border border-white/5 p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <Target className="w-5 h-5 text-primary" />
                    FRICK'S DAILY MISSIONS
                  </h3>
                  <div className="space-y-1">
                    <ChecklistItem>Log in to earn your daily XP (+10 XP)</ChecklistItem>
                    <ChecklistItem>Check the Dashboard for new ideas</ChecklistItem>
                    <ChecklistItem>Write or edit some PHP code (+5 XP each save)</ChecklistItem>
                    <ChecklistItem>Complete one tutorial lesson (+30-50 XP)</ChecklistItem>
                    <ChecklistItem>Challenge Frack to beat your level!</ChecklistItem>
                  </div>
                </div>

                {/* Quick Start Guide */}
                <div className="bg-[#16191F] border border-white/5 p-6 md:col-span-2">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <Rocket className="w-5 h-5 text-primary" />
                    FRICK'S QUICK START
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-black/30 p-4 border border-primary/20">
                      <div className="text-2xl font-black text-primary mb-2">1</div>
                      <h4 className="text-white font-bold mb-1">START HERE</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        Go to "GUIDES" and do the "PHP for Beginners" tutorial. Learn how to make the computer say "Hello!"
                      </p>
                    </div>
                    <div className="bg-black/30 p-4 border border-primary/20">
                      <div className="text-2xl font-black text-primary mb-2">2</div>
                      <h4 className="text-white font-bold mb-1">TRY CODING</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        Open the Code Deck on any site. Find index.php and change what it says. Click SAVE!
                      </p>
                    </div>
                    <div className="bg-black/30 p-4 border border-primary/20">
                      <div className="text-2xl font-black text-primary mb-2">3</div>
                      <h4 className="text-white font-bold mb-1">ASK BUBBA</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        Stuck? Ask Dad! He knows the answers. Or check the ? icons in the app.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* FRACK's Guide */}
            <TabsContent value="frack" className="mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Role Card */}
                <div className="bg-[#16191F] border border-green-500/30 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Wrench className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        FRACK - THE OPERATOR
                      </h3>
                      <p className="text-xs text-muted-foreground font-mono">Age 10 | Systems Runner</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground font-mono text-sm mb-4">
                    You make things RUN! You're in charge of containers, deployments, and making sure everything works. 
                    Frick designs it, you ship it!
                  </p>
                  <div className="bg-black/30 p-4 border border-white/5">
                    <h4 className="text-white font-bold mb-2">YOUR SUPERPOWERS:</h4>
                    <ul className="space-y-1 text-sm font-mono text-muted-foreground">
                      <li>• Deploying containers</li>
                      <li>• Managing sites (start/stop/restart)</li>
                      <li>• Reading logs to find problems</li>
                      <li>• Training ML models</li>
                    </ul>
                  </div>
                </div>

                {/* Daily Checklist */}
                <div className="bg-[#16191F] border border-white/5 p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <Target className="w-5 h-5 text-green-400" />
                    FRACK'S DAILY MISSIONS
                  </h3>
                  <div className="space-y-1">
                    <ChecklistItem>Log in to earn your daily XP (+10 XP)</ChecklistItem>
                    <ChecklistItem>Check all containers are RUNNING (green)</ChecklistItem>
                    <ChecklistItem>Deploy or restart a site (+25 XP)</ChecklistItem>
                    <ChecklistItem>Train a new ML model in Robot Gym (+50 XP)</ChecklistItem>
                    <ChecklistItem>Help Frick when he's stuck!</ChecklistItem>
                  </div>
                </div>

                {/* Quick Start Guide */}
                <div className="bg-[#16191F] border border-white/5 p-6 md:col-span-2">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <Rocket className="w-5 h-5 text-green-400" />
                    FRACK'S QUICK START
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-black/30 p-4 border border-green-500/20">
                      <div className="text-2xl font-black text-green-400 mb-2">1</div>
                      <h4 className="text-white font-bold mb-1">DEPLOY FIRST</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        Go to "DEPLOYMENTS" and click "NEW DEPLOYMENT". Give it a cool name. Watch it go RUNNING!
                      </p>
                    </div>
                    <div className="bg-black/30 p-4 border border-green-500/20">
                      <div className="text-2xl font-black text-green-400 mb-2">2</div>
                      <h4 className="text-white font-bold mb-1">ROBOT GYM</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        Go to "ROBOT GYM" and train a model. Pick a name, click TRAIN, watch the accuracy!
                      </p>
                    </div>
                    <div className="bg-black/30 p-4 border border-green-500/20">
                      <div className="text-2xl font-black text-green-400 mb-2">3</div>
                      <h4 className="text-white font-bold mb-1">CHECK LOGS</h4>
                      <p className="text-xs text-muted-foreground font-mono">
                        Click on a site, go to LOGS tab. See what the container is doing. Green = good!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* BUBBA's Guide */}
            <TabsContent value="bubba" className="mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Role Card */}
                <div className="bg-[#16191F] border border-blue-500/30 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        BUBBA - THE MENTOR
                      </h3>
                      <p className="text-xs text-muted-foreground font-mono">Dad | Wise Guide</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground font-mono text-sm mb-4">
                    You're the wise one who keeps the garage running. Guide Frick & Frack, answer their questions,
                    and help them when they're stuck. You also handle the "grown-up" stuff like domains and settings.
                  </p>
                  <div className="bg-black/30 p-4 border border-white/5">
                    <h4 className="text-white font-bold mb-2">YOUR RESPONSIBILITIES:</h4>
                    <ul className="space-y-1 text-sm font-mono text-muted-foreground">
                      <li>• Teaching concepts they don't understand</li>
                      <li>• Managing ai.daniejoye.com domain</li>
                      <li>• Reviewing their code together</li>
                      <li>• Celebrating their wins!</li>
                    </ul>
                  </div>
                </div>

                {/* Admin Checklist */}
                <div className="bg-[#16191F] border border-white/5 p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <Target className="w-5 h-5 text-blue-400" />
                    BUBBA'S WEEKLY TASKS
                  </h3>
                  <div className="space-y-1">
                    <ChecklistItem>Review kids' XP and progress</ChecklistItem>
                    <ChecklistItem>Do a "code review" session together</ChecklistItem>
                    <ChecklistItem>Introduce one new concept per week</ChecklistItem>
                    <ChecklistItem>Check ai.daniejoye.com is working</ChecklistItem>
                    <ChecklistItem>Celebrate level-ups with a treat!</ChecklistItem>
                  </div>
                </div>

                {/* Teaching Tips */}
                <div className="bg-[#16191F] border border-white/5 p-6 md:col-span-2">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    <BookOpen className="w-5 h-5 text-blue-400" />
                    BUBBA'S TEACHING TIPS
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-black/30 p-4 border border-blue-500/20">
                        <h4 className="text-blue-400 font-bold mb-2">FOR FRICK (8)</h4>
                        <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                          <li>• Keep explanations SHORT and use analogies</li>
                          <li>• "Variables are like labeled boxes"</li>
                          <li>• Let him experiment - breaking things is learning!</li>
                          <li>• Celebrate every small win loudly</li>
                          <li>• 15-20 minute coding sessions max</li>
                        </ul>
                      </div>
                      <div className="bg-black/30 p-4 border border-green-500/20">
                        <h4 className="text-green-400 font-bold mb-2">FOR FRACK (10)</h4>
                        <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                          <li>• Challenge him with "harder" problems</li>
                          <li>• Explain the "why" behind things</li>
                          <li>• Let him teach Frick - teaching = learning</li>
                          <li>• Give him ownership of deployments</li>
                          <li>• 30-45 minute sessions are fine</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-black/30 p-4 border border-primary/20">
                        <h4 className="text-primary font-bold mb-2">PAIR PROGRAMMING RULES</h4>
                        <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                          <li>1. One types (driver), one watches (navigator)</li>
                          <li>2. Switch roles every 10 minutes</li>
                          <li>3. Navigator can't touch the keyboard!</li>
                          <li>4. Talk through what you're doing</li>
                          <li>5. Disagreements? Try both ways!</li>
                        </ul>
                      </div>
                      <div className="bg-black/30 p-4 border border-white/10">
                        <h4 className="text-white font-bold mb-2">DOMAIN MANAGEMENT</h4>
                        <p className="text-xs font-mono text-muted-foreground mb-2">
                          ai.daniejoye.com is your family's domain. To set it up:
                        </p>
                        <ul className="space-y-1 text-xs font-mono text-muted-foreground">
                          <li>1. Click "Link domain" in Emergent</li>
                          <li>2. Enter: ai.daniejoye.com</li>
                          <li>3. Follow DNS instructions</li>
                          <li>4. Wait 5-15 min for propagation</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Family Challenge */}
          <div className="mt-8 bg-gradient-to-r from-primary/10 via-green-500/10 to-blue-500/10 border border-primary/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Zap className="w-5 h-5 text-primary" />
              THIS WEEK'S FAMILY CHALLENGE
            </h3>
            <p className="text-muted-foreground font-mono text-sm mb-4">
              Work together to complete this challenge. Each person has a role!
            </p>
            <div className="bg-black/30 p-4 border border-white/10">
              <h4 className="text-xl font-bold text-white mb-2">"Build a Hello World Page"</h4>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-primary font-bold text-xs">F</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    FRICK: Write the PHP code that says "Hello from the [YourLastName] Family!"
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-green-400 font-bold text-xs">F</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    FRACK: Deploy the container and make sure it's RUNNING
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-400 font-bold text-xs">B</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">
                    BUBBA: Help if stuck, then visit the page together!
                  </p>
                </div>
              </div>
              <div className="text-center mt-4">
                <span className="text-primary font-mono text-sm">REWARD: +100 FAMILY XP 🎉</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FamilyGuidePage;
